/**
 * Promotion Block for Editor.js
 * Creates email-safe promotional sections with discount codes
 */

import { BlockTool } from '@editorjs/editorjs';

export interface PromotionBlockData {
  badge: string;
  headline: string;
  discount: string;
  promoCode: string;
  buttonText: string;
  buttonUrl: string;
}

export default class PromotionBlock implements BlockTool {
  private data: PromotionBlockData;
  private wrapper: HTMLElement | null = null;

  static get toolbox() {
    return {
      title: 'Promotion',
      icon: '<svg width="17" height="15" viewBox="0 0 17 15" xmlns="http://www.w3.org/2000/svg"><path d="M8.5 1L10.5 5.5L15.5 6L12 9.5L13 14.5L8.5 12L4 14.5L5 9.5L1.5 6L6.5 5.5L8.5 1Z" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>',
    };
  }

  constructor({ data }: { data: PromotionBlockData }) {
    this.data = {
      badge: data.badge || 'SPECIAL OFFER',
      headline: data.headline || 'Fall Wine Sale',
      discount: data.discount || '20% OFF',
      promoCode: data.promoCode || 'FALL20',
      buttonText: data.buttonText || 'Shop Now',
      buttonUrl: data.buttonUrl || 'https://www.ejswineshop.com',
    };
  }

  render(): HTMLElement {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('email-promotion-block');
    this.wrapper.style.cssText = `
      background-color: #D4A657;
      padding: 32px;
      text-align: center;
      border-radius: 8px;
      margin: 16px 0;
    `;

    // Badge
    const badge = document.createElement('span');
    badge.contentEditable = 'true';
    badge.textContent = this.data.badge;
    badge.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: #ffffff;
      background-color: #722f37;
      padding: 4px 12px;
      border-radius: 12px;
      display: inline-block;
      margin: 0 0 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    badge.addEventListener('input', () => {
      this.data.badge = badge.textContent || '';
    });

    // Headline
    const headline = document.createElement('h2');
    headline.contentEditable = 'true';
    headline.textContent = this.data.headline;
    headline.style.cssText = `
      font-size: 28px;
      font-weight: 700;
      color: #2b2b2b;
      margin: 0 0 12px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    headline.addEventListener('input', () => {
      this.data.headline = headline.textContent || '';
    });

    // Discount
    const discount = document.createElement('p');
    discount.contentEditable = 'true';
    discount.textContent = this.data.discount;
    discount.style.cssText = `
      font-size: 36px;
      font-weight: 700;
      color: #722f37;
      margin: 0 0 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    discount.addEventListener('input', () => {
      this.data.discount = discount.textContent || '';
    });

    // Promo code container
    const promoContainer = document.createElement('p');
    promoContainer.style.cssText = `
      font-size: 14px;
      line-height: 22px;
      color: #2b2b2b;
      margin: 0 0 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    promoContainer.innerHTML = 'Use code: ';

    const promoCode = document.createElement('strong');
    promoCode.contentEditable = 'true';
    promoCode.textContent = this.data.promoCode;
    promoCode.style.outline = 'none';
    promoCode.addEventListener('input', () => {
      this.data.promoCode = promoCode.textContent || '';
    });
    promoContainer.appendChild(promoCode);

    // Button
    const button = document.createElement('a');
    button.href = this.data.buttonUrl;
    button.contentEditable = 'true';
    button.textContent = this.data.buttonText;
    button.style.cssText = `
      background-color: #2b2b2b;
      color: #ffffff;
      font-size: 16px;
      font-weight: 600;
      padding: 12px 32px;
      border-radius: 4px;
      text-decoration: none;
      display: inline-block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      outline: none;
    `;
    button.addEventListener('input', () => {
      this.data.buttonText = button.textContent || '';
    });
    button.addEventListener('click', (e) => e.preventDefault());

    this.wrapper.appendChild(badge);
    this.wrapper.appendChild(document.createElement('br'));
    this.wrapper.appendChild(headline);
    this.wrapper.appendChild(discount);
    this.wrapper.appendChild(promoContainer);
    this.wrapper.appendChild(button);

    return this.wrapper;
  }

  save(): PromotionBlockData {
    return this.data;
  }

  renderSettings(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.padding = '8px';

    // Button URL
    const urlLabel = document.createElement('label');
    urlLabel.textContent = 'Button URL:';
    urlLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const urlInput = document.createElement('input');
    urlInput.type = 'url';
    urlInput.value = this.data.buttonUrl;
    urlInput.placeholder = 'https://...';
    urlInput.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;';
    urlInput.addEventListener('change', () => {
      this.data.buttonUrl = urlInput.value;
    });

    wrapper.appendChild(urlLabel);
    wrapper.appendChild(urlInput);

    return wrapper;
  }
}
